import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { CalendarDays } from "lucide-react";

const DatePicker = ({ precision = "day", value, onChange, disabled }) => {
  const classes = useStyles();
  const inputRef = useRef();

  const [internalValue, setInternalValue] = useState(value || "");

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleIconClick = () => {
    if (inputRef.current && !disabled) {
      inputRef.current.showPicker?.(); // for browsers that support it
      inputRef.current.focus(); // fallback for others
      inputRef.current.click(); // often needed to trigger the picker
    }
  };

  const getInputType = () => {
    switch (precision) {
      case "second":
        return "datetime-local";
      case "minute":
        return "datetime-local";
      case "hour":
        return "datetime-local";
      case "day":
        return "date";
      case "month":
        return "month";
      case "year":
        return "number";
      default:
        return "date";
    }
  };

  const getStepValue = () => {
    switch (precision) {
      case "second":
        return 1; // 1 second
      case "minute":
        return 60; // 60 seconds = 1 minute
      case "hour":
        return 3600; // 3600 seconds = 1 hour
      default:
        return undefined; // For day/month/year
    }
  };

  return (
    <div className={classes.container}>
      <input type={getInputType()} step={getStepValue()} className={classes.datePicker} value={internalValue} onChange={handleChange} disabled={disabled} ref={inputRef} />
      <CalendarDays className={classes.icon} onClick={handleIconClick} />
    </div>
  );
};

DatePicker.propTypes = {
  precision: PropTypes.oneOf(["second", "minute", "hour", "day", "month", "year"]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

const useStyles = makeStyles(() => ({
  container: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  datePicker: {
    height: "40px",
    width: "100%",
    backgroundColor: "transparent",
    border: "1px solid #DCDCDC",
    borderRadius: 4,
    outline: "none",
    padding: "0px 12px",
    fontSize: "16px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    transition: "border-color 0.2s ease",
    "&:focus-visible": {
      borderColor: "#C3996B",
    },
    "&:hover": {
      cursor: "text",
    },
    "&:focus": {
      borderColor: "#C3996B",
    },
    "&:disabled": {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
    },

    // remove default calendar icon
    "&::-webkit-calendar-picker-indicator": {
      display: "none",
      "-webkit-appearance": "none",
    },
  },
  icon: {
    width: "20px",
    height: "20px",
    strokeWidth: "1.4px",

    position: "absolute",
    right: "10px",

    color: "#0C0E29",
    cursor: "pointer",
    pointerEvents: "auto",
  },
}));

export default DatePicker;
